<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import { dateFormat } from 'shared/helpers/timeHelper';
import Table from 'dashboard/components/table/Table.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LabelPill from 'dashboard/components-next/label/LabelPill.vue';

const props = defineProps({
  labels: {
    type: Array,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const EMPTY_CELL = '-';

function displayText(value) {
  if (value === null || value === undefined) return EMPTY_CELL;
  const str = String(value).trim();
  return str.length ? str : EMPTY_CELL;
}

function formatTimestamp(ts) {
  if (ts === null || ts === undefined || ts === '') return EMPTY_CELL;
  try {
    return dateFormat(Number(ts), 'LLL d, yyyy, h:mm a');
  } catch {
    return EMPTY_CELL;
  }
}

function formatSource(source) {
  const raw = displayText(source);
  if (raw === EMPTY_CELL) return EMPTY_CELL;
  if (String(source).toLowerCase() === 'dashboard') {
    return t('LABEL_MGMT.SOURCE.DASHBOARD');
  }
  return raw;
}

function renderNameCell(label) {
  return h(LabelPill, { label, dense: true });
}

const rows = computed(() => props.labels.map(label => ({ label })));

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor(row => row.label.title ?? '', {
    id: 'title',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    cell: ({ row }) => renderNameCell(row.original.label),
    size: 220,
  }),
  columnHelper.accessor(row => row.label.description ?? '', {
    id: 'description',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    cell: ({ row }) =>
      h(
        'span',
        {
          class:
            'line-clamp-1 max-w-xs whitespace-pre-wrap break-words text-sm text-n-slate-12',
        },
        displayText(row.original.label.description)
      ),
    size: 200,
  }),
  columnHelper.accessor(row => row.label.created_by_name ?? '', {
    id: 'created_by_name',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.CREATED_BY'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'whitespace-nowrap text-sm text-n-slate-12' },
        displayText(row.original.label.created_by_name)
      ),
    size: 140,
  }),
  columnHelper.accessor(row => row.label.source ?? '', {
    id: 'source',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.SOURCE'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'whitespace-nowrap text-sm text-n-slate-12' },
        formatSource(row.original.label.source)
      ),
    size: 120,
  }),
  columnHelper.accessor(row => Number(row.label.created_at) || 0, {
    id: 'created_at',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.CREATED_ON'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'whitespace-nowrap text-sm text-n-slate-12' },
        formatTimestamp(row.original.label.created_at)
      ),
    size: 190,
  }),
  columnHelper.accessor(row => row.label.updated_by_name ?? '', {
    id: 'updated_by_name',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.LAST_EDITED_BY'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'whitespace-nowrap text-sm text-n-slate-12' },
        displayText(row.original.label.updated_by_name)
      ),
    size: 140,
  }),
  columnHelper.accessor(row => Number(row.label.updated_at) || 0, {
    id: 'updated_at',
    header: t('LABEL_MGMT.LIST.TABLE_HEADER.LAST_EDITED_ON'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'whitespace-nowrap text-sm text-n-slate-12' },
        formatTimestamp(row.original.label.updated_at)
      ),
    size: 190,
  }),
  columnHelper.display({
    id: 'actions',
    header: () =>
      h(
        'span',
        { class: 'text-base font-bold text-n-slate-12 normal-case' },
        t('LABEL_MGMT.LIST.TABLE_HEADER.ACTION')
      ),
    enableSorting: false,
    cell: ({ row }) => {
      const label = row.original.label;
      return h(
        'div',
        { class: 'flex flex-row items-center justify-end gap-2' },
        [
          h(Button, {
            slate: true,
            sm: true,
            icon: 'i-woot-edit-pen',
            title: t('LABEL_MGMT.FORM.EDIT'),
            isLoading: props.loading[label.id],
            onClick: () => emit('edit', label),
          }),
          h(Button, {
            slate: true,
            sm: true,
            icon: 'i-woot-bin',
            class: 'hover:enabled:bg-n-ruby-2 hover:enabled:text-n-ruby-11',
            title: t('LABEL_MGMT.FORM.DELETE'),
            isLoading: props.loading[label.id],
            onClick: () => emit('delete', label),
          }),
        ]
      );
    },
    size: 88,
  }),
]);

const table = useVueTable({
  get data() {
    return rows.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  getRowId: row => String(row.label.id),
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});
</script>

<template>
  <Table :table="table" type="compact" panel="soft" />
</template>
